import { t } from '@lingui/macro'
import { FieldState, FormState } from 'formstate'
import { first } from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import moment from 'moment'
import React from 'react'
import { v4 as uuidv4 } from 'uuid'
import {
  CurrenciesQuery,
  Currency,
  DailyMenu,
  DailyMenuDayCategory,
  DailyMenuDayCategoryItem,
  DailyMenuDayCategoryTypeEnum,
  DailyMenuLogoSizeTypeEnum,
  GetDailyMenuByIdQuery,
  SoupPricingTypeEnum,
} from '../../../../__generated__/graphql'
import { DailyMenuModel } from '../../../stores/models'
import { getFileFromUrl } from '../../../utils/getFileFromUrl'
import {
  StoreDailyMenuCategoryForm,
  StoreDailyMenuCategoryItemForm,
  StoreDailyMenuDayForm,
  StoreDailyMenuDaysForm,
} from './StoreDailyMenuDaysForm'
import { StoreDailyMenuSettingsForm } from './StoreDailyMenuSettingsForm'

export const MoveElementDirectionType = {
  UP: 'UP',
  DOWN: 'DOWN',
} as const

export class StoreCreateDailyMenu {
  id?: DailyMenuModel['id']
  dailyMenu?: DailyMenuModel
  currencies: CurrenciesQuery['currencies'] = []
  settingsForm = new StoreDailyMenuSettingsForm()
  daysForm = new StoreDailyMenuDaysForm()
  daysFormBackup: StoreDailyMenuDayForm[] = []
  takeScreenshot = false
  redirectToPreview = false
  previews: Array<React.RefObject<HTMLDivElement> | null> = []

  constructor(id?: DailyMenuModel['id']) {
    makeObservable(this, {
      id: observable,
      dailyMenu: observable,
      settingsForm: observable,
      daysForm: observable,
      selectedCurrency: computed,
      setLogo: action.bound,
      trimDays: action.bound,
      takeScreenshot: observable.ref,
      redirectToPreview: observable,
      previews: observable,
    })
    this.id = id
  }

  get selectedCurrency(): Currency | undefined {
    return this.currencies.find(
      (currency) => currency.id === this.settingsForm.currencyId.$
    )
  }

  fromCurrenciesQuery(src: CurrenciesQuery) {
    this.currencies = src.currencies
  }

  setLogo = (src: string) => {
    if (this.dailyMenu) {
      this.dailyMenu.logo = src
    }
  }

  fillSettingsForm(payload: GetDailyMenuByIdQuery['dailyMenu']) {
    const getImageFile = async () => {
      if (payload?.logo) {
        this.settingsForm.logo.onChange(
          await getFileFromUrl(payload?.logo, 'image.jpg')
        )
      }
    }

    getImageFile()

    this.settingsForm.title.onChange(payload!.title!)
    this.settingsForm.from.onChange(payload!.from!)
    this.settingsForm.to.onChange(payload!.to!)
    this.settingsForm.restaurantName.onChange(payload!.restaurantName!)
    this.settingsForm.description.onChange(payload!.description!)
    this.settingsForm.bottomDescription.onChange(payload!.bottomDescription!)
    this.settingsForm.currencyId.onChange(
      payload!.currency?.id ?? first(this.currencies)?.id ?? '1'
    )
    this.settingsForm.soupCanBeOrderedSeparately.onChange(
      payload!.soupCanBeOrderedSeparately ?? false
    )
    this.settingsForm.soupPricedInMainDish.onChange(
      payload!.soupPricedInMainDish ?? false
    )
    this.settingsForm.soupPricingType.onChange(
      payload!.soupPricingType ?? SoupPricingTypeEnum.SeparatedFromMainDish
    )
    this.settingsForm.showRestaurantName.onChange(
      payload!.showRestaurantName ?? false
    )
    this.settingsForm.showAllergens.onChange(payload!.showAllergens ?? false)
    this.settingsForm.showPortionSize.onChange(
      payload!.showPortionSize ?? false
    )
    this.settingsForm.logoSizeType.onChange(
      payload!.logoSizeType ?? DailyMenuLogoSizeTypeEnum.Large
    )
  }

  public createCategory = (
    category: DailyMenuDayCategory
  ): StoreDailyMenuCategoryForm => {
    return new FormState({
      id: new FieldState<string>(category.id ?? '').validators(),
      title: new FieldState<string>(category.title ?? '').validators(),
      type: new FieldState<DailyMenuDayCategoryTypeEnum>(
        category.type
      ).validators(),
      enabled: new FieldState<boolean>(category.enabled ?? true).validators(),
      order: new FieldState<number>(category.order ?? 1).validators(),
      items: new FormState(
        category.items.map((item) => this.createCategoryItem(item))
      ),
    })
  }

  public createCategoryItem = (
    item: DailyMenuDayCategoryItem
  ): StoreDailyMenuCategoryItemForm => {
    return new FormState({
      id: new FieldState<string>(item.id ?? '').validators(),
      title: new FieldState<string>(item.title ?? '').validators(),
      portionSize: new FieldState<string>(item.portionSize ?? '').validators(),
      price: new FieldState<string>(item.price ?? '').validators(),
      priceCombo: new FieldState<string>(item.priceCombo ?? '').validators(),
      order: new FieldState<number>(item.order ?? 1).validators(),
      allergens: new FieldState<string[]>(
        item.allergens.map((allergen: { id: any }) => allergen.id)
      ).validators(),
    })
  }

  public deleteCategory = (categoryId: string, day: StoreDailyMenuDayForm) => {
    const categoryIdX = day.categories.$.findIndex(
      (i) => i.$.id?.value === categoryId
    )

    day.categories.$.splice(categoryIdX, 1)

    day.categories.$.forEach((category, index) => {
      category.$.order.onChange(index + 1)
    })
  }

  public moveCategory = (
    categoryId: string,
    direction: typeof MoveElementDirectionType[keyof typeof MoveElementDirectionType],
    day: StoreDailyMenuDayForm
  ) => {
    const categoryIdX = day.categories.$.findIndex(
      (category) => category.$.id?.value === categoryId
    )
    const targetIdX =
      direction === MoveElementDirectionType.UP
        ? categoryIdX - 1
        : categoryIdX + 1

    if (targetIdX < 0 || targetIdX >= day.categories.$.length) return

    // Swap the categories in the array
    const currentCategory = day.categories.$[categoryIdX]
    const targetCategory = day.categories.$[targetIdX]

    day.categories.$[categoryIdX] = targetCategory
    day.categories.$[targetIdX] = currentCategory

    // Update the order values
    day.categories.$.forEach((category, index) => {
      category.$.order.onChange(index + 1)
    })
  }

  public deleteCategoryItem = (
    categoryId: string,
    itemId: string,
    day: StoreDailyMenuDayForm
  ) => {
    const categoryIdX = day.categories.$.findIndex(
      (i) => i.$.id?.value === categoryId
    )
    const itemIdX = day.categories.$[categoryIdX].$.items.$.findIndex(
      (i) => i.$.id?.value === itemId
    )
    day.categories.$[categoryIdX].$.items.$.splice(itemIdX, 1)

    day.categories.$[categoryIdX].$.items.$.forEach((item, index) => {
      item.$.order.onChange(index + 1)
    })
  }

  public moveCategoryItem = (
    categoryId: string,
    itemId: string,
    direction: typeof MoveElementDirectionType[keyof typeof MoveElementDirectionType],
    day: StoreDailyMenuDayForm
  ) => {
    const categoryIdX = day.categories.$.findIndex(
      (category) => category.$.id?.value === categoryId
    )
    const itemIdX = day.categories.$[categoryIdX].$.items.$.findIndex(
      (item) => item.$.id?.value === itemId
    )
    const targetIdX =
      direction === MoveElementDirectionType.UP ? itemIdX - 1 : itemIdX + 1

    if (
      targetIdX < 0 ||
      targetIdX >= day.categories.$[categoryIdX].$.items.$.length
    )
      return

    // Swap the items in the array
    const currentItem = day.categories.$[categoryIdX].$.items.$[itemIdX]
    const targetItem = day.categories.$[categoryIdX].$.items.$[targetIdX]

    day.categories.$[categoryIdX].$.items.$[itemIdX] = targetItem
    day.categories.$[categoryIdX].$.items.$[targetIdX] = currentItem

    // Update the order values
    day.categories.$[categoryIdX].$.items.$.forEach((item, index) => {
      item.$.order.onChange(index + 1)
    })
  }

  public trimDays = () => {
    const days = this.daysForm.days.slice(0, 7)
    this.daysForm.days = days
  }

  fillDaysForm(days: DailyMenu['days']) {
    this.daysForm = new StoreDailyMenuDaysForm()

    days?.forEach((day) => {
      const dayForm = new StoreDailyMenuDayForm()

      dayForm.date.onChange(day.date)
      dayForm.enabled.onChange(day.enabled)

      day.categories.forEach((category) => {
        const categoryForm = this.createCategory(category)

        dayForm.categories.$.push(categoryForm)
      })

      this.daysForm.days.push(dayForm)
    })
  }

  rebuildDaysForm() {
    const diff =
      moment(this.settingsForm.to.value).diff(
        moment(this.settingsForm.from.value),
        'days'
      ) + 1

    this.daysForm.days.forEach((day) => {
      if (
        !this.daysFormBackup.find(
          (backupDay) => backupDay.date.value === day.date.value
        )
      ) {
        this.daysFormBackup.push(day)
      }
    })

    this.daysForm = new StoreDailyMenuDaysForm()

    for (let i = 0; i < diff; i++) {
      const date = moment(this.settingsForm.from.value)
        .add(i, 'days')
        .format('YYYY-MM-DD')

      const backupDay = this.daysFormBackup.find(
        (backupDay) => backupDay.date.value === date
      )

      if (backupDay) {
        this.daysForm.days.push(backupDay)
        continue
      }

      const dayForm = new StoreDailyMenuDayForm()

      dayForm.date.onChange(date)

      this.daysForm.days.push(dayForm)
    }
  }

  // Prefill the default categories when creating menu
  prefillDefaultCategories() {
    if (this.daysForm.days.length > 0) {
      this.daysForm.days.forEach((day) => {
        if (!day.categories.$.length) {
          day.categories.$.push(
            this.createCategory({
              id: uuidv4(),
              dailyMenuDayId: '0',
              title: t`Soups` ?? 'Soups',
              type: DailyMenuDayCategoryTypeEnum.Soups,
              enabled: true,
              order: 1,
              items: [],
            }),
            this.createCategory({
              id: uuidv4(),
              dailyMenuDayId: '0',
              title: t`Meals` ?? 'Meals',
              type: DailyMenuDayCategoryTypeEnum.Meals,
              enabled: true,
              order: 2,
              items: [],
            }),
            this.createCategory({
              id: uuidv4(),
              dailyMenuDayId: '0',
              title: t`Desserts` ?? 'Desserts',
              type: DailyMenuDayCategoryTypeEnum.Desserts,
              enabled: true,
              order: 3,
              items: [],
            })
          )
        }
      })
    }
  }
}

export const StoreCreateDailyMenuCtx =
  React.createContext<StoreCreateDailyMenu>(null as any as StoreCreateDailyMenu)
